import { portalStoreToken, PortalShareData, PortalUrls, usePortalStore } from '@servicetitan/cp-ui';
import { BodyText, Spinner } from '@servicetitan/design-system';
import { useDependencies } from '@servicetitan/react-ioc';
import { Loader } from '@servicetitan/web-components';
import { observer } from 'mobx-react';
import { FC, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PortalAuthStore } from './modules/auth/stores/portal-auth.store';
import { useAuthHistory } from './modules/auth/utils/use-auth-history';
import { PortalLayout } from './modules/common/components/layout/portal-layout';
import { AppSettingsStore } from './modules/common/stores/app-settings.store';
import { EnvStore } from './modules/common/stores/env.store';
import { DashboardModule } from './modules/dashboard/components/dashboard.module';
import { usePortalConfig } from './utils/config';

interface MfeLoaderProps {
    name: string;
    basename: string;
}

interface AuthorizedMfeLoaderProps extends MfeLoaderProps {
    allowAnonEstimates?: boolean;
    allowAnonPayments?: boolean;
    allowAnonServiceAgreements?: boolean;
}

const AuthorizedMfeLoader: FC<AuthorizedMfeLoaderProps> = ({
    allowAnonEstimates,
    allowAnonPayments,
    allowAnonServiceAgreements,
    ...rest
}) => {
    const [
        { authorized, isAnonymousEstimates, isAnonymousPayments, isAnonymousServiceAgreements },
    ] = useDependencies(PortalAuthStore);
    const authHistory = useAuthHistory();

    const allowAnonAccess =
        (!!allowAnonPayments && isAnonymousPayments) ||
        (!!allowAnonEstimates && isAnonymousEstimates) ||
        (!!allowAnonServiceAgreements && isAnonymousServiceAgreements);

    if (!authorized && !allowAnonAccess) {
        return <Navigate to={authHistory.path('login')} />;
    }

    return <MfeLoader {...rest} />;
};

const MfeLoader: FC<MfeLoaderProps> = observer(({ basename, name }) => {
    const [{ culture, settings, user }, envStore] = useDependencies(portalStoreToken, EnvStore);
    let { mfeTag } = usePortalConfig();

    useEffect(() => {
        envStore.loadData();
    }, [envStore]);

    if (mfeTag === 'dev') {
        mfeTag = envStore?.settings?.environmentTag ?? 'dev';
    }
    const data: Record<keyof PortalShareData, string> = useMemo(
        () => ({
            user: JSON.stringify(user?.data),
            settings: JSON.stringify(settings),
            culture: JSON.stringify(culture),
        }),
        [culture, settings, user]
    );

    let packagePath = `https://unpkg.servicetitan.com/@servicetitan/${name}`;
    let fallbackPackagePath;

    if (mfeTag) {
        fallbackPackagePath = packagePath;
        packagePath = `${packagePath}@${mfeTag}`;
    }

    if (process.env.NODE_ENV === 'development') {
        const port =
            {
                'customer-portal-invoices': '9010',
                'customer-portal-estimates': '9020',
                'customer-portal-memberships': '9040',
                'customer-portal-service-agreements': '9050',
                'customer-portal-jobs': '9060',
            }[name] ?? '8090';

        packagePath = `http://foobar.localhost:${port}`;
        fallbackPackagePath = undefined;
    }

    return (
        <Loader
            key={name}
            className="w-100"
            src={packagePath}
            fallbackSrc={fallbackPackagePath}
            basename={basename}
            loadingFallback={<Spinner size="tiny" />}
            errorFallback={<BodyText>Please refresh this page, or try again later.</BodyText>}
            data={data}
        />
    );
});

export const AuthorizedApp = observer(() => {
    const { user } = usePortalStore();
    const pickMembership = user?.pickMembership;
    const [
        { authorized, authorizedLoginless },
        { membershipEnabled, proposalsEstimatesEnabled, serviceAgreementsEnabled },
    ] = useDependencies(PortalAuthStore, AppSettingsStore);
    const authHistory = useAuthHistory();

    const membershipsRoot =
        membershipEnabled && pickMembership
            ? pickMembership(
                  PortalUrls.memberships.rootMembership,
                  PortalUrls.memberships.rootContracts
              )
            : undefined;
    return (
        <PortalLayout>
            <Routes>
                <Route
                    path={`${PortalUrls.jobs.root}/*`}
                    element={
                        <MfeLoader name="customer-portal-jobs" basename={PortalUrls.jobs.root} />
                    }
                />
                <Route
                    path={`${PortalUrls.invoices.root}/*`}
                    element={
                        <AuthorizedMfeLoader
                            name="customer-portal-invoices"
                            basename={PortalUrls.invoices.root}
                            allowAnonPayments
                        />
                    }
                />
                {(proposalsEstimatesEnabled || authorizedLoginless) && (
                    <Route
                        path={`${PortalUrls.proposals.root}/*`}
                        element={
                            <AuthorizedMfeLoader
                                name="customer-portal-estimates"
                                basename={PortalUrls.proposals.root}
                                allowAnonEstimates
                            />
                        }
                    />
                )}
                {!!membershipsRoot && (
                    <Route
                        path={`${membershipsRoot}/*`}
                        element={
                            <AuthorizedMfeLoader
                                name="customer-portal-memberships"
                                basename={membershipsRoot}
                            />
                        }
                    />
                )}
                {(serviceAgreementsEnabled || authorizedLoginless) && (
                    <Route
                        path={`${PortalUrls.serviceAgreements.root}/*`}
                        element={
                            <AuthorizedMfeLoader
                                name="customer-portal-service-agreements"
                                basename={PortalUrls.serviceAgreements.root}
                                allowAnonServiceAgreements
                            />
                        }
                    />
                )}

                <Route
                    path="/*"
                    element={
                        authorized ? (
                            <DashboardModule />
                        ) : (
                            <Navigate to={authHistory.path('login')} />
                        )
                    }
                />
            </Routes>
        </PortalLayout>
    );
});
